/**
 * Bottom Navigation - Premium wellness app navigation
 * Fixed bottom navigation bar for member app
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Calendar, BookOpen, Sparkles, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const navItems = [
  { id: 'schedule', label: 'Schedule', icon: Calendar, path: '/schedule' },
  { id: 'bookings', label: 'Bookings', icon: BookOpen, path: '/my-bookings' },
  { id: 'wallet', label: 'Wallet', icon: Sparkles, path: '/wallet' },
  { id: 'account', label: 'Account', icon: User, path: '/account' },
];

export default function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();

  // Hide the bottom nav for anonymous/non-logged-in users — every item
  // routes to a members-only screen anyway. Showing them would just
  // bounce the visitor to /login on tap. While auth is still resolving
  // we keep the bar hidden to avoid a flash.
  if (isLoading || !isAuthenticated) return null;

  // Don't show on admin pages, home, login, register, instructor pages, 
  // class detail pages, or first-time booking flow pages
  const hideOnPaths = [
    '/', 
    '/login', 
    '/register', 
    '/admin',
    '/book-first-class',  // Landing page for first-time users
    '/offer',             // Alternative landing page
    '/book',              // Direct booking page
    '/booking-confirmed', // Confirmation page
    '/contact',           // Contact page
    '/corporate',         // Corporate landing page
    '/franchise',          // Franchise landing page
    '/private-classes',     // Private classes landing page
    '/strength'            // Strength training landing page
  ];
  const isAdminPath = location.pathname.startsWith('/admin');
  const isInstructorPath = location.pathname.startsWith('/instructor');
  const isClassDetailPath = location.pathname.startsWith('/class/');
  const isBookingFlow = location.pathname.startsWith('/book');
  const isLandingPage = location.pathname.startsWith('/lp/');  // New dynamic landing pages
  const isCorporatePage = location.pathname.startsWith('/corporate');  // Corporate pages
  const isFranchisePage = location.pathname.startsWith('/franchise');  // Franchise pages
  const isPrivateClassesPage = location.pathname.startsWith('/private-classes');  // Private classes (all variants)
  const isStrengthPage = location.pathname.startsWith('/strength');  // Strength training (all variants)
  const isCompleteProfile = location.pathname === '/complete-profile';
  
  if (hideOnPaths.includes(location.pathname) || isAdminPath || isInstructorPath || isClassDetailPath || isBookingFlow || isLandingPage || isCorporatePage || isFranchisePage || isPrivateClassesPage || isStrengthPage || isCompleteProfile) {
    return null;
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-t border-border/50 pb-safe">
      <div className="flex items-center justify-around h-16 max-w-lg mx-auto px-4">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || 
            (item.id === 'bookings' && location.pathname === '/bookings');
          
          return (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center justify-center gap-1 px-4 py-2 rounded-xl transition-all ${
                isActive 
                  ? 'text-primary' 
                  : 'text-muted-foreground hover:text-primary/70'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <div className={`p-1.5 rounded-xl transition-colors ${
                isActive ? 'bg-primary/10' : ''
              }`}>
                <Icon className={`w-5 h-5 ${isActive ? 'stroke-[2.5]' : ''}`} />
              </div>
              <span className={`text-[10px] font-medium tracking-wide ${
                isActive ? 'text-primary' : ''
              }`}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
